import { useMemo, useState } from 'react'
import { Button, Input, Select } from '@/shared/ui'
import {
  integrationsApi,
  useAction,
  type ActionDef,
  type Catalog,
  type Inbound,
  type InboundInput,
  type InboundResult,
  type Mapping,
  type SchemaField,
} from '@/entities/integration'
import {
  CheckIcon,
  ChoiceCard,
  CopyField,
  Drawer,
  InboxIcon,
  JsonView,
  Label,
  PlayIcon,
  PlusIcon,
  Segmented,
  StatusPill,
  Step,
  timeAgo,
  TrashIcon,
} from './kit'
import { InboundResultView } from './Journal'
import s from './integrations.module.css'


export function InboundTab({ catalog, links }: { catalog: Catalog; links: Inbound[] }) {
  const [editing, setEditing] = useState<{ link: Inbound | null; action?: string } | null>(null)
  const actionTitle = useMemo(() => Object.fromEntries(catalog.actions.map((a) => [a.action, a.title])), [catalog])
  return (
    <>
      <div className={s.sectionHead}>
        <div>
          <h2 className={s.sectionTitle}>Приём заявок</h2>
          <p className={s.sectionText}>
            Ваша система присылает данные на ссылку — и мы сразу действуем: добавляем номер в обзвон, звоним по заявке с
            сайта, отмечаем «не звонить». Одна ссылка — одно действие.
          </p>
        </div>
        {links.length > 0 && (
          <Button onClick={() => setEditing({ link: null })}>
            <span className={s.btnRow}><PlusIcon /> Новая ссылка</span>
          </Button>
        )}
      </div>

      {links.length === 0 ? (
        <div className={s.starters}>
          {catalog.actions.map((a) => (
            <button key={a.action} type="button" className={s.starter} onClick={() => setEditing({ link: null, action: a.action })}>
              <span className={s.starterTitle}>{a.title}</span>
              <span className={s.starterText}>{a.description}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className={s.cards}>
          {links.map((l) => (
            <div key={l.id} className={`${s.card} ${l.enabled ? '' : s.cardOff}`} role="button" tabIndex={0}
              onClick={() => setEditing({ link: l })} onKeyDown={(e) => e.key === 'Enter' && setEditing({ link: l })}>
              <div className={s.cardTop}>
                <div className={s.cardTitles}>
                  <h3 className={s.cardTitle}>{l.name}</h3>
                  <div className={s.cardSub}>{actionTitle[l.action] ?? l.action}</div>
                </div>
                <StatusPill status={l.enabled ? 'on' : 'off'} />
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <CopyField value={l.url} label="Ссылка" />
              </div>
              <div className={s.cardFoot}>
                <span>
                  Запросов: {l.stats.total ?? 0} · ошибок: {l.stats.failed ?? 0}
                </span>
                <span>{l.updatedAt ? `изменено ${timeAgo(l.updatedAt)}` : ''}</span>
              </div>
            </div>
          ))}
          <button type="button" className={s.addCard} onClick={() => setEditing({ link: null })}>
            <span className={s.addCardIcon}>
              <PlusIcon />
            </span>
            Новая ссылка
          </button>
        </div>
      )}

      {editing && <InboundEditor catalog={catalog} link={editing.link} initialAction={editing.action} onClose={() => setEditing(null)} />}
    </>
  )
}

/* ------------------------------------------------------------- Editor */

function InboundEditor({
  catalog,
  link,
  initialAction,
  onClose,
}: {
  catalog: Catalog
  link: Inbound | null
  initialAction?: string
  onClose: () => void
}) {
  const firstAction = catalog.actions.find((a) => a.action === initialAction) ?? catalog.actions[0]
  const [form, setForm] = useState<InboundInput>(() =>
    link
      ? { name: link.name, enabled: link.enabled, action: link.action, authMode: link.authMode, config: link.config }
      : {
          name: firstAction?.title ?? 'Входящая ссылка',
          enabled: true,
          action: firstAction?.action ?? 'contact.upsert',
          authMode: 'token',
          config: {
            schema: [],
            mapping: {},
            itemsPath: '',
            campaignId: 0,
            idempotencyPath: '',
            rateLimit: 60,
            signatureHeader: 'X-Signature',
            queryName: 'token',
            sampleFormat: 'json',
            sample: JSON.stringify(firstAction?.sample ?? {}, null, 2),
          },
        },
  )
  const [created, setCreated] = useState<Inbound | null>(link)
  const [testBody, setTestBody] = useState(form.config.sample || '{}')
  const [testRes, setTestRes] = useState<InboundResult | null>(null)

  const action: ActionDef | undefined = catalog.actions.find((a) => a.action === form.action)
  const sampleFormat = form.config.sampleFormat ?? 'json'
  const set = <K extends keyof InboundInput>(k: K, v: InboundInput[K]) => setForm((f) => ({ ...f, [k]: v }))
  const setCfg = (patch: Partial<InboundInput['config']>) => setForm((f) => ({ ...f, config: { ...f.config, ...patch } }))

  const guess = useAction(() => integrationsApi.guessInbound(form.action, form.config.sample, sampleFormat), 'Разобрали пример — проверьте сопоставление')
  const save = useAction(() => integrationsApi.saveInbound(created?.id ?? null, form), created ? 'Сохранено' : 'Ссылка готова')
  const remove = useAction(() => integrationsApi.deleteInbound(created!.id), 'Удалено')
  const rotate = useAction((what: 'url' | 'secret') => integrationsApi.rotateInbound(created!.id, what), 'Готово — старое значение больше не работает')
  const test = useAction(() => integrationsApi.testInbound(created!.id, testBody, sampleFormat))

  const doGuess = () =>
    guess.mutate(undefined, {
      onSuccess: (r) => setCfg({ schema: r.schema, itemsPath: r.itemsPath, mapping: r.mapping }),
    })

  const sourcePaths = useMemo(() => {
    const rel = form.config.schema.map((f) => f.path)
    return [{ value: '', label: '— не брать —' }, ...rel.map((p) => ({ value: p, label: p }))]
  }, [form.config.schema])

  const setMap = (key: string, m: Mapping) => setCfg({ mapping: { ...form.config.mapping, [key]: m } })

  const changeSampleFormat = (next: 'json' | 'form') => {
    let sample = form.config.sample
    if (next === 'form') {
      try {
        sample = formExample(JSON.parse(sample))
      } catch {
        sample = formExample(action?.sample ?? {})
      }
    } else if (sampleFormat !== 'json') {
      sample = JSON.stringify(action?.sample ?? {}, null, 2)
    }
    setCfg({ sampleFormat: next, sample, schema: [], mapping: {}, itemsPath: '' })
    setTestBody(sample)
    setTestRes(null)
  }

  const needsCampaign = action?.settings?.includes('campaign')
  const needsControl = form.action === 'campaign.control'

  const doSave = () =>
    save.mutate(undefined, {
      onSuccess: (r) => {
        if (!created) {
          setCreated(r)
          setTestBody(form.config.sample || '{}')
        } else onClose()
      },
    })

  const footer = (
    <>
      {created && (
        <Button variant="ghost" loading={remove.isPending}
          onClick={() => window.confirm(`Удалить «${created.name}»? Ссылка сразу перестанет работать.`) && remove.mutate(undefined, { onSuccess: onClose })}>
          Удалить
        </Button>
      )}
      <span className={s.spacer} />
      <Button variant="secondary" onClick={onClose}>
        {created && !link ? 'Готово' : 'Отмена'}
      </Button>
      <Button onClick={doSave} loading={save.isPending}>
        <span className={s.btnRow}><CheckIcon /> {created ? 'Сохранить' : 'Создать ссылку'}</span>
      </Button>
    </>
  )

  let n = 1
  return (
    <Drawer title={link ? link.name : 'Новая входящая ссылка'} subtitle="Ваша система присылает данные — мы выполняем действие"
      icon={<InboxIcon />} onClose={onClose} footer={footer} wide>
      <Step n={n++} title="Что делать с данными" hint="Одна ссылка — одно действие. Нужно несколько — сделайте несколько ссылок.">
        <div className={s.templates}>
          {catalog.actions.map((a) => (
            <button key={a.action} type="button" className={`${s.tpl} ${form.action === a.action ? s.tplActive : ''}`}
              disabled={!!link && link.action !== a.action}
              onClick={() => {
                setForm((f) => ({
                  ...f,
                  action: a.action,
                  name: catalog.actions.some((x) => x.title === f.name) ? a.title : f.name,
                  config: { ...f.config, sampleFormat: 'json', sample: JSON.stringify(a.sample ?? {}, null, 2), mapping: {}, schema: [] },
                }))
              }}>
              <span className={s.tplTitle}>
                {a.title}
              </span>
              <span className={s.tplText}>{a.description}</span>
            </button>
          ))}
        </div>
        {needsCampaign && (
          <div className={s.formRow}>
            <Label>{form.action === 'campaign.remove' ? 'Из какого обзвона убрать' : 'В какой обзвон'}</Label>
            <Select value={String(form.config.campaignId)} onChange={(e) => setCfg({ campaignId: Number(e.target.value) })}
              options={[
                { value: '0', label: form.action === 'campaign.remove' ? 'Из всех обзвонов' : 'Выберите обзвон' },
                ...catalog.campaigns.map((c) => ({ value: String(c.id), label: c.name })),
              ]} />
          </div>
        )}
        {needsControl && (
          <div className={`${s.alert} ${s.alertInfo}`}>
            Обзвон выберите выше, а в запросе — команду: <b>start</b> — запустить, <b>pause</b> — пауза, <b>stop</b> — остановить.
          </div>
        )}
      </Step>

      <Step n={n++} title="Название">
        <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
      </Step>

      <Step n={n++} title="Формат и пример входящих данных" hint="Выберите формат, вставьте реальное тело запроса — мы сами найдём телефон и остальные поля.">
        <div className={s.formRow}>
          <Label>Формат запроса</Label>
          <Select
            value={sampleFormat}
            onChange={(e) => changeSampleFormat(e.target.value as 'json' | 'form')}
            options={[
              { value: 'json', label: 'JSON' },
              { value: 'form', label: 'Форма / Bitrix24' },
            ]}
          />
        </div>
        <textarea className={s.textarea} rows={8} value={form.config.sample} spellCheck={false}
          onChange={(e) => setCfg({ sample: e.target.value })} />
        <div className={s.condActions}>
          <Button variant="secondary" size="sm" onClick={doGuess} loading={guess.isPending}>
            Разобрать пример
          </Button>
          {form.config.itemsPath && (
            <span className={s.chip}>
              пачка: <code className={s.mono}>{form.config.itemsPath}</code> (до 1000 записей)
            </span>
          )}
        </div>
      </Step>

      {action && (
        <Step n={n++} title="Что откуда брать" hint="Для каждого поля укажите, где оно лежит во входящих данных. После «Разобрать пример» — просто выберите из списка.">
          {action.fields.map((f) => {
            const m = form.config.mapping[f.key] ?? { source: '' }
            return (
              <div key={f.key} className={s.mapRow}>
                <div>
                  <div className={s.tplTitle}>
                    {f.title}
                    {f.required && <span style={{ color: 'var(--danger)' }}> *</span>}
                  </div>
                  {f.hint && <div className={s.tplText}>{f.hint}</div>}
                </div>
                <span className={s.mapArrow}>←</span>
                {sourcePaths.length > 1 ? (
                  <Select value={m.source} onChange={(e) => setMap(f.key, { ...m, source: e.target.value })} options={sourcePaths} />
                ) : (
                  <Input value={m.source} placeholder={`например, ${f.key === "phone" ? "lead.phone" : f.key === "name" ? "lead.name" : "lead." + f.key}`} onChange={(e) => setMap(f.key, { ...m, source: e.target.value })} />
                )}
                <Select value={m.transform ?? ''} onChange={(e) => setMap(f.key, { ...m, transform: e.target.value })}
                  options={[
                    { value: '', label: 'как есть' },
                    { value: 'phone', label: 'телефон' },
                    { value: 'trim', label: 'без пробелов' },
                    { value: 'lower', label: 'строчные' },
                    { value: 'digits', label: 'только цифры' },
                  ]} />
              </div>
            )
          })}
        </Step>
      )}

      {form.config.schema.length > 0 && (
        <Step n={n++} title="Проверка данных" hint="Если обязательное поле не пришло или в нём не то — запрос отклоним и объясним почему.">
          <SchemaEditor schema={form.config.schema} onChange={(v) => setCfg({ schema: v })} types={catalog.schemaTypes} />
        </Step>
      )}

      <Step n={n++} title="Защита" hint="Чтобы чужие не могли слать запросы на вашу ссылку.">
        <Segmented
          value={form.authMode}
          onChange={(v) => set('authMode', v)}
          options={[
            { value: 'token', label: 'Ключ в ссылке' },
            { value: 'header', label: 'Ключ в заголовке' },
            { value: 'hmac', label: 'Подпись HMAC' },
            { value: 'none', label: 'Без защиты' },
          ]}
        />
        {form.authMode === 'none' && <div className={`${s.alert} ${s.alertBad}`}>Любой, кто узнает ссылку, сможет добавлять номера. Не рекомендуем.</div>}
        {(form.authMode === 'hmac' || form.authMode === 'header') && (
          <div className={s.formRow}>
            <Label hint={form.authMode === 'hmac' ? 'HMAC-SHA256 от тела запроса нашим секретом, в hex' : undefined}>{form.authMode === 'hmac' ? 'Заголовок с подписью' : 'Заголовок с ключом'}</Label>
            <Input value={form.config.signatureHeader} onChange={(e) => setCfg({ signatureHeader: e.target.value })} />
          </div>
        )}
        <div className={s.grid2}>
          <div className={s.formRow}>
            <Label hint="Повтор с тем же значением не выполнится второй раз">Поле с ID заявки (от дублей)</Label>
            <Input value={form.config.idempotencyPath} placeholder="например, lead.id" onChange={(e) => setCfg({ idempotencyPath: e.target.value })} />
          </div>
          <div className={s.formRow}>
            <Label>Не больше запросов в минуту</Label>
            <Input type="number" min={1} max={600} value={form.config.rateLimit} onChange={(e) => setCfg({ rateLimit: Number(e.target.value) || 60 })} />
          </div>
        </div>
      </Step>

      {created ? (
        <>
          <Step n={n++} title="Ваша ссылка" hint="Вставьте её в свою систему — в настройки вебхука, роботов Битрикса или в Tilda.">
            <CopyField value={created.url} label="Ссылка" />
            {created.authMode !== 'token' && created.authMode !== 'none' && created.secret && (
              <div className={s.formRow}>
                <Label>{created.authMode === 'hmac' ? 'Секрет для подписи' : `Ключ (заголовок ${form.config.signatureHeader || 'X-Api-Key'})`}</Label>
                <CopyField value={created.secret} secret label="Ключ" />
              </div>
            )}
            <div className={s.condActions}>
              <Button size="sm" variant="secondary" loading={rotate.isPending}
                onClick={() => window.confirm('Выпустить новую ссылку? Старая перестанет работать сразу.') && rotate.mutate('url', { onSuccess: (r) => setCreated(r) })}>
                Новая ссылка
              </Button>
              {created.authMode !== 'token' && created.authMode !== 'none' && (
                <Button size="sm" variant="secondary" loading={rotate.isPending}
                  onClick={() => window.confirm('Выпустить новый ключ? Старый перестанет работать сразу.') && rotate.mutate('secret', { onSuccess: (r) => setCreated(r) })}>
                  Новый ключ
                </Button>
              )}
            </div>
            <details>
              <summary className={s.addLink} style={{ cursor: 'pointer' }}>Пример запроса (curl)</summary>
              <pre className={s.code} style={{ marginTop: 8 }}>{curlExample(created, form.config.sample, sampleFormat)}</pre>
            </details>
          </Step>
          <Step n={n++} title="Проверка" hint="Пробный запуск: покажем, что бы сделали, но ничего не изменим.">
            <textarea className={s.textarea} rows={6} value={testBody} spellCheck={false} onChange={(e) => setTestBody(e.target.value)} />
            <div className={s.condActions}>
              <Button variant="secondary" size="sm" loading={test.isPending}
                onClick={() => test.mutate(undefined, { onSuccess: (r) => setTestRes(r) })}>
                <PlayIcon /> Проверить без изменений
              </Button>
            </div>
            {testRes && <InboundResultView result={testRes} status={testRes.status === 'ok' ? 'dry_run' : testRes.status} />}
          </Step>
        </>
      ) : (
        <div className={`${s.alert} ${s.alertInfo}`}>Нажмите «Создать ссылку» — появится адрес, который нужно вставить в вашу систему, и пробный запуск.</div>
      )}
    </Drawer>
  )
}

function SchemaEditor({ schema, onChange, types }: { schema: SchemaField[]; onChange: (v: SchemaField[]) => void; types: string[] }) {
  const TYPE_TITLE: Record<string, string> = {
    string: 'текст',
    phone: 'телефон',
    number: 'число',
    boolean: 'да/нет',
    date: 'дата',
    enum: 'из списка',
    object: 'объект',
    array: 'список',
  }
  const upd = (i: number, p: Partial<SchemaField>) => onChange(schema.map((f, j) => (j === i ? { ...f, ...p } : f)))
  return (
    <div className={s.table}>
      {schema.map((f, i) => (
        <div key={f.path} className={s.mapRow} style={{ padding: '6px 10px' }}>
          <div>
            <code className={s.mono}>{f.path}</code>
            {f.example && <div className={s.tplText}>например: {f.example}</div>}
          </div>
          <Select value={f.type} onChange={(e) => upd(i, { type: e.target.value })}
            options={types.map((t) => ({ value: t, label: TYPE_TITLE[t] ?? t }))} />
          <label className={s.chip} style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={f.required} onChange={(e) => upd(i, { required: e.target.checked })} /> обязательно
          </label>
          <button type="button" className={s.iconBtn} onClick={() => onChange(schema.filter((_, j) => j !== i))}>
            <TrashIcon />
          </button>
          {f.type === 'enum' && (
            <div style={{ gridColumn: '1 / -1' }}>
              <Input placeholder="допустимые значения через запятую" value={(f.enum ?? []).join(', ')}
                onChange={(e) => upd(i, { enum: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function formExample(value: unknown) {
  const params = new URLSearchParams()
  const visit = (node: unknown, path: string) => {
    if (Array.isArray(node)) {
      node.forEach((item, index) => visit(item, `${path}[${index}]`))
      return
    }
    if (node && typeof node === 'object') {
      Object.entries(node as Record<string, unknown>).forEach(([key, item]) =>
        visit(item, path ? `${path}[${key}]` : key),
      )
      return
    }
    params.append(path, node == null ? '' : String(node))
  }
  visit(value, '')
  return params.toString()
}

function curlExample(l: Inbound, sample: string, format: 'json' | 'form') {
  let body = sample.trim() || (format === 'json' ? '{}' : '')
  if (format === 'json') {
    try {
      body = JSON.stringify(JSON.parse(body))
    } catch {
      /* как есть */
    }
  }
  const contentType = format === 'form' ? 'application/x-www-form-urlencoded' : 'application/json'
  const h = [`-H 'Content-Type: ${contentType}'`]
  if (l.authMode === 'header') h.push(`-H '${l.config.signatureHeader || 'X-Api-Key'}: <ключ>'`)
  if (l.authMode === 'hmac') h.push(`-H '${l.config.signatureHeader || 'X-Signature'}: <hmac-sha256 тела>'`)
  return `curl -X POST '${l.url}' \\\n  ${h.join(' \\\n  ')} \\\n  -d '${body.replace(/'/g, "'\\''")}'`
}

void JsonView
void ChoiceCard
